import React from "react";
import {
    Button as OmegaButton,
    IconButton as OmegaIconButton,
    type ButtonProps as OmegaButtonProps,
    type IconButtonProps as OmegaIconButtonProps,
} from "@omega/ui";

/** Legacy-вариант оформления shared Button. */
export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

/** Размер кнопки. */
export type ButtonSize = "sm" | "md" | "lg";

/** Пропсы {@link Button}. */
export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color"> {
    /** Вариант оформления. @default "primary" */
    variant?: ButtonVariant;
    /** Размер. @default "md" */
    size?: ButtonSize;
    /** Показать индикатор загрузки и заблокировать клик. */
    loading?: boolean;
    /** Текст (для icon-only используйте {@link ButtonIcon}). */
    children?: React.ReactNode;
    /** Слот слева от текста. */
    startSlot?: React.ReactNode;
    /** Слот справа от текста. */
    endSlot?: React.ReactNode;
    /** Растянуть кнопку на всю ширину контейнера. @default false */
    fullWidth?: boolean;
}

/** Пропсы icon-only кнопки {@link ButtonIcon}. */
export interface ButtonIconProps extends Omit<ButtonProps, "children" | "startSlot" | "endSlot"> {
    children: React.ReactNode;
    /** Обязателен для icon-only. */
    "aria-label": string;
}

type OmegaAppearance = Pick<OmegaButtonProps, "variant" | "tone">;

function mapLegacyVariant(variant: ButtonVariant): OmegaAppearance {
    switch (variant) {
        case "primary":
            return { variant: "solid", tone: "accent" };
        case "secondary":
            return { variant: "outline", tone: "accent" };
        case "ghost":
            return { variant: "ghost", tone: "accent" };
        case "danger":
            return { variant: "solid", tone: "danger" };
        default: {
            const _exhaustive: never = variant;
            return _exhaustive;
        }
    }
}

/**
 * Shared UI кнопка: compatibility facade над `@omega/ui` Button.
 *
 * Legacy mapping: `primary→solid+accent`, `secondary→outline+accent`,
 * `ghost→ghost+accent`, `danger→solid+danger`; `startSlot`/`endSlot` → `startIcon`/`endIcon`.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    {
        variant = "primary",
        size = "md",
        loading = false,
        disabled,
        className,
        children,
        startSlot,
        endSlot,
        fullWidth = false,
        type = "button",
        ...rest
    },
    ref,
) {
    const appearance = mapLegacyVariant(variant);

    return (
        <OmegaButton
            {...rest}
            ref={ref}
            type={type}
            size={size}
            loading={loading}
            disabled={disabled}
            className={className}
            startIcon={startSlot}
            endIcon={endSlot}
            fullWidth={fullWidth}
            variant={appearance.variant}
            tone={appearance.tone}
        >
            {children}
        </OmegaButton>
    );
});

/**
 * Icon-only кнопка: facade над `@omega/ui` IconButton (`Button.Icon`).
 */
export const ButtonIcon = React.forwardRef<HTMLButtonElement, ButtonIconProps>(function ButtonIcon(
    { variant = "ghost", size = "md", loading = false, disabled, className, children, type = "button", ...rest },
    ref,
) {
    const appearance = mapLegacyVariant(variant);
    const iconButtonProps: OmegaIconButtonProps = {
        ...rest,
        type,
        size,
        loading,
        disabled,
        className,
        variant: appearance.variant,
        tone: appearance.tone,
        children,
    };

    return <OmegaIconButton ref={ref} {...iconButtonProps} />;
});

Button.displayName = "Button";
ButtonIcon.displayName = "ButtonIcon";

/** Compound API: `Button.Icon`. */
const ButtonCompound = Object.assign(Button, { Icon: ButtonIcon });
export default ButtonCompound;
